import { Component, ElementRef, OnDestroy, OnInit, ChangeDetectionStrategy, inject, output, input, ChangeDetectorRef, signal, linkedSignal } from '@angular/core';
import {Subject} from 'rxjs';
import {Client, Project, User, CurrencyOption} from '@app/modules/shared/models';
import {effectiveUtmParams} from '@app/modules/shared/validators/tracked-url.validator';
import {SbUrlInputComponent} from '@app/_components/unified/sb-url-input/sb-url-input.component';
import {SbCtaUrlInputComponent} from '@app/_components/unified/sb-url-input/sb-cta-url-input.component';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {TitleService} from '@app/services/title.service';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import {Constants} from '@app/consts';

import * as routes from '@app/routes';
import {select, Store} from '@ngrx/store';
import {
  ClientsListAll,
  getClientError,
  getClients,
  getLoggedInUser,
  getProject,
  getProjectError,
  getProjectIndustries,
  getProjectSlug,
  getProjectSuccess,
  IAuthenticationState,
  IClientState,
  IProjectState,
  ProjectCreate,
  ProjectGet,
  ProjectIndustries,
  ProjectUpdate,
  ProjectValidateSlug,
  ResetProjectState
} from '@app/stores';
import {takeUntil} from 'rxjs/operators';
import moment from 'moment-timezone';
import {ProjectService} from '@app/services/project.service';
import {SbSelectItem} from '@app/_components/unified/sb-select/sb-select-base.component';
import { PageShellComponent } from '../../../_components/page-shell/page-shell.component';
import { SbInputFormComponent, SbInputFormSectionComponent, SbInputFormActionsDirective } from '../../../_components/unified/sb-form/sb-form.component';
import { SbInputImageComponent } from '../../../_components/unified/sb-input/sb-input-image.component';
import { SbInputTextComponent } from '../../../_components/unified/sb-input/sb-input-text.component';
import { SbInputSelectComponent } from '../../../_components/unified/sb-select/sb-select.component';
import { SbInputNumberComponent } from '../../../_components/unified/sb-input/sb-input-number.component';
import { SbInputTimezoneComponent } from '../../../_components/unified/sb-select/sb-input-timezone.component';
import { SbInputTimeComponent } from '../../../_components/unified/sb-input/sb-input-time.component';
import { SbComplexMultipleComponent } from '../../../_components/unified/sb-complex-multiple/sb-complex-multiple.component';
import { SbInputComplexRowDirective } from '../../../_components/unified/sb-complex-multiple/sb-complex-row.directive';
import { SbInputUrlComponent } from '../../../_components/unified/sb-input/sb-input-url.component';
import { SbCheckboxComponent } from '../../../_components/unified/sb-checkbox/sb-checkbox.component';
import { SbInputEmailComponent } from '../../../_components/unified/sb-input/sb-input-email.component';
import { SbInputIpComponent } from '../../../_components/unified/sb-input/sb-input-ip.component';
import { SbTextareaComponent } from '../../../_components/unified/sb-textarea/sb-textarea.component';
import { SbButtonComponent } from '../../../_components/unified/sb-button/sb-button.component';

// The design system's date input reads and writes the day as YYYY-MM-DD whatever the browser's
// locale, which is what the server stores. Important dates still come back as MM/DD/YYYY.
const DAY_FORMATS = ['YYYY-MM-DD', 'MM/DD/YYYY'];
const TIME_FORMATS = ['hh:mm A', 'h:mm A', 'HH:mm'];

const toDay = (value: unknown): string => {
  if (!value) {
    return '';
  }
  const day = moment(String(value), DAY_FORMATS, true);
  return day.isValid() ? day.format('YYYY-MM-DD') : '';
};

const toTime = (value: unknown): string => {
  if (!value) {
    return '';
  }
  const time = moment(String(value).trim(), TIME_FORMATS, true);
  return time.isValid() ? time.format('hh:mm A') : '';
};

// Settings that inherit a global default hold 'default' in the form, because a select cannot show
// a null option as chosen. They go back to null on save.
const INHERIT = 'default';
const toInheritedOption = (value: unknown): string =>
  value === null || value === undefined || value === '' ? INHERIT : String(value);

@Component({
  selector: 'app-event-add',
  templateUrl: './project-add.component.html',
  styleUrls: ['./project-add.component.css'],
  providers: [Constants],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SbUrlInputComponent, SbCtaUrlInputComponent, PageShellComponent, SbInputFormComponent, ReactiveFormsModule, SbInputFormSectionComponent, SbInputImageComponent, SbInputTextComponent, SbInputSelectComponent, SbInputNumberComponent, SbInputTimezoneComponent, SbInputTimeComponent, SbComplexMultipleComponent, SbInputComplexRowDirective, FormsModule, SbInputUrlComponent, SbCheckboxComponent, SbInputEmailComponent, SbInputIpComponent, SbTextareaComponent, SbInputFormActionsDirective, SbButtonComponent]
})
export class ProjectAddComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private projectStore = inject<Store<IProjectState>>(Store);
  private clientStore = inject<Store<IClientState>>(Store);
  constants = inject(Constants);
  private toastr = inject(ToastrService);
  private titleService = inject(TitleService);
  private elRef = inject(ElementRef);
  private projectService = inject(ProjectService);


  readonly currentUser = signal<User>(undefined);
  readonly planners = signal<Client[]>([]);
  /** Fixed controls, plus the custom variables by name. */
  addProjectForm: FormGroup<{[key: string]: FormControl<any>}>;
  submitted = false;
  slugdata: string;
  title = 'Add Project';
  /** Set by the route param on the edit page, or assigned as an input when
   *  opened in a modal by EntityFormModalService (input takes precedence). */
  readonly projectIdInput = input<number>(null, {alias: 'projectId'});
  readonly projectId = linkedSignal(() => this.projectIdInput());
  /** True when opened inside an NgbModal via EntityFormModalService. */
  readonly isModal = input(false);
  readonly modalDone = output<'saved' | 'cancelled'>();
  project: Project;
  returnUrl;
  public step;

  readonly industryList = signal<SbSelectItem[]>([]);
  eventList: SbSelectItem[] = [
    {id: 'in-person', name: 'In Person'},
    {id: 'virtual', name: 'Virtual'},
    {id: 'hybrid', name: 'Hybrid'},
    {id: 'membership', name: 'Membership'}
  ];
  isPaidList: SbSelectItem[] = [
    {id: 'free', name: 'Free'},
    {id: 'paid', name: 'Paid'},
    {id: 'hybrid', name: 'Hybrid'}
  ];
  inheritedSwitchList: SbSelectItem[] = [
    {id: INHERIT, name: 'Default (global setting)'},
    {id: 'true', name: 'Enabled'},
    {id: 'false', name: 'Disabled'}
  ];
  widgetVersionList: SbSelectItem[] = [
    {id: INHERIT, name: 'Default (global setting)'},
    {id: 'v1', name: 'v1 - Current (stable)'},
    {id: 'v2', name: 'v2 - Latest (beta)'}
  ];
  renderingSystemList: SbSelectItem[] = [
    {id: INHERIT, name: 'Default (global setting)'},
    {id: 'webRunner', name: 'Web Runner'},
    {id: 'cloudflare', name: 'Cloudflare Browser Rendering'},
    {id: 'browserRendererManager', name: 'Browser Renderer Manager'}
  ];
  readonly currencyList = signal<CurrencyOption[]>([]);
  customFields = ['custom_1', 'custom_2', 'custom_3', 'custom_4', 'custom_5',
    'custom_6', 'custom_7', 'custom_8', 'custom_9', 'custom_10'];
  importantDateEmptyItem = {important_text: '', important_date: ''};
  socialEmptyItem = {social_label: '', social_value: ''};
  displayDatetimeFormatHelp = `Controls how dates and times appear in templates. Leave blank for the default: September 29, 2026 at 11:40 AM. A time zone is shown only when both the value and your format include one (add z to your format).

MMMM D, YYYY [at] h:mm A → September 29, 2026 at 11:40 AM (default, verbose)
MMMM D, YYYY [at] h:mm A z → September 29, 2026 at 11:40 AM GMT (add z to show the time zone)
ddd, MMM D, YYYY h:mm A → Tue, Sep 29, 2026 11:40 AM (with weekday)
DD/MM/YYYY HH:mm → 29/09/2026 11:40 (day first, 24-hour)
MM/DD/YYYY h:mm A → 09/29/2026 11:40 AM (US style)
MMM D, YYYY → Sep 29, 2026 (date only)`;

  unsubscriber = new Subject();

  constructor() {
    this.projectStore.dispatch(ResetProjectState({params: {error: '', project: null}}));
    this.route.paramMap
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(params => {
        if (this.isModal()) {
          // Opened as a modal: projectId comes from the input, not the URL.
          return;
        }
        this.projectId.set(params.get('projectId') ? +params.get('projectId') : null);
        const projectId = this.projectId();
        if (projectId) {
          this.title = 'Edit Project';
          this.titleService.set(this.title);
          this.projectStore.dispatch(ProjectGet({projectId: projectId}));
        }
        this.cdr.markForCheck();
      });
    this.route.queryParams
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(params => {
        this.returnUrl = params.returnUrl;
      });
    this.subscribeStore();
    this.titleService.set(this.title);
  }

  get f() {
    return this.addProjectForm.controls;
  }

  get isSuperAdmin(): boolean {
    return this.currentUser()?.role_user?.[0]?.role?.slug === 'super-admin';
  }

  get clientId(): number | null {
    return this.addProjectForm?.get('client_id')?.value || null;
  }

  /**
   * An end before its start is cleared, as the end picker's minimum would have prevented it. Once the
   * start's entry is done, not on a date typing passes through, which would lose a stored end.
   */
  clearEndBefore(start: unknown, ...ends: string[]) {
    const endDate = this.addProjectForm.get(ends[0]).value;
    if (start && endDate && endDate < start) {
      this.addProjectForm.patchValue(Object.fromEntries(ends.map(end => [end, ''])));
    }
  }

  /** The message under a field once the form has been submitted, '' while it is valid. */
  fieldError(name: string, requiredMessage = 'This field is required', patternMessage = 'Please enter a valid URL'): string {
    const control = this.addProjectForm?.get(name);
    if (!this.submitted || !control?.errors) {
      return '';
    }
    // A half-typed date writes '' and is invalid: its field says what is missing, not that it is required.
    if (control.errors.required && !control.errors.sbInputInvalid) {
      return requiredMessage;
    }
    if (control.errors.pattern) {
      return patternMessage;
    }
    return '';
  }

  private normalizeInheritedBoolean(value: any): boolean | null {
    if (value === true || value === 'true') {
      return true;
    }
    if (value === false || value === 'false') {
      return false;
    }
    return null;
  }

  private normalizeInheritedWidgetVersion(value: any): 'v1' | 'v2' | null {
    return value === 'v1' || value === 'v2' ? value : null;
  }

  subscribeStore() {
    this.authenticationStore.pipe(select(getLoggedInUser))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(user => {
        if (user) {
          this.currentUser.set(user);
        }
      }
      );

    this.projectStoreSubscribe();
    this.userStoreSubscribe();
  }

  projectStoreSubscribe() {
    this.projectStore.pipe(select(getProjectError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });

    this.projectStore.pipe(select(getProjectSlug))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(slug => {
        if (slug && slug === 'invalid') {
          this.addProjectForm.controls['slug'].setValue('');
          this.addProjectForm.controls['hashtag'].setValue('');
          this.toastr.error('Please change the slug, it\'s already taken', 'Error');
          this.cdr.markForCheck();
        }
      });

    this.projectStore.pipe(select(getProject))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(project => {
        if (project) {
          const projectId = this.projectId();
          if (projectId) {
            if (project.id === projectId) {
              this.project = Object.assign({}, project);
              this.patchProject();
              // The form was patched with the project.
              this.cdr.markForCheck();
            }
          } else {
            if (this.isModal()) {
              if (this.submitted) {
                this.submitted = false;
                this.modalDone.emit('saved');
              }
              return;
            }
            this.router.navigate([routes.campaigns({projectId: project.id})]);
          }
        }
      });

    this.projectStore.pipe(select(getProjectSuccess))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(success => {
        if (success && this.projectId() && this.submitted) {
          this.submitted = false;
          if (this.isModal()) {
            this.modalDone.emit('saved');
            return;
          }
          if (this.returnUrl && this.returnUrl !== this.router.url) {
            this.router.navigateByUrl(this.returnUrl);
          } else {
            this.router.navigate(['projects']);
          }
        }
      });

    this.projectStore.pipe(select(getProjectIndustries))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(industries => {
        if (industries) {
          // A project stores its industry id as a string.
          this.industryList.set((industries as any[]).map(industry => ({id: String(industry.id), name: industry.name})));
        }
      });
  }

  userStoreSubscribe() {
    this.clientStore.pipe(select(getClientError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error);
        }
      });
    this.clientStore.pipe(select(getClients))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(clients => {
        if (clients && clients.length > 0) {
          this.planners.set(clients);
        }
      });
  }


  ngOnInit() {
    const projectId = this.projectId();
    if (this.isModal() && projectId) {
      // Inputs are assigned after the constructor's route subscription ran,
      // so trigger the edit-mode load here.
      this.title = 'Edit Project';
      this.titleService.set(this.title);
      this.projectStore.dispatch(ProjectGet({projectId: projectId}));
    }
    this.step = {title: 'Project', params: {}};
    this.loadAllPlanners();
    this.getIndustries();
    this.loadCurrencies();
    this.addProjectForm = this.formBuilder.group<{[key: string]: any}>({
      title: ['', Validators.required],
      client_id: [null, this.currentUser()?.can('change-client', 'project') ? Validators.required : []],
      venue: [''],
      image: [null],
      event_type: ['', Validators.required],
      is_paid: ['', Validators.required],
      industry: [''],
      currency: ['USD'],
      timezone: [null],
      display_datetime_format: [''],
      start_date: ['', Validators.required],
      start_time: [''],
      end_date: ['', Validators.required],
      end_time: [''],
      activation_starts_at: ['', Validators.required],
      activation_ends_at: ['', Validators.required],
      registration_url: ['', Validators.required],
      website_url: ['', [Validators.required, Validators.pattern(this.constants.urlRegex)]],
      // The field is hidden, so it has no validator: an old saved value nobody can see must not block a save.
      google_utm_params: [''],
      emails_source_message: [''],
      register_average_price: [''],
      report_analysis: [''],
      hashtag: [''],
      facebook: ['', Validators.pattern(this.constants.urlRegex)],
      twitter: ['', Validators.pattern(this.constants.urlRegex)],
      linkedin: ['', Validators.pattern(this.constants.urlRegex)],
      instagram: ['', Validators.pattern(this.constants.urlRegex)],
      description: [''],
      slug: ['', Validators.required],
      created_by: [this.currentUser().id, [Validators.required]],
      third_party_project_id: [''],
      // Always at least one row: the server replaces a project's rows only when it gets some.
      dates: [[{...this.importantDateEmptyItem}]],
      socials: [[{...this.socialEmptyItem}]],
      ...Object.fromEntries(this.customFields.map(field => [field, ['']])),
      exclude_emails: [''],
      exclude_ips: [''],
      notes: [''],
      sessions_can_be_updated_influencer: [false],
      booth_can_be_updated_influencer: [false],
      rendering_system: [INHERIT],
      widget_cdn_enabled: [INHERIT],
      widget_version: [INHERIT]
    });
    if (this.project) {
      this.patchProject();
    }
  }

  /**
   * What the tracking link adds to this project's call to action, as far as the form knows: the
   * project's own Google UTM parameters, else the client's. The server knows the default too.
   */
  callToActionUtmParams(): string | null {
    const client = this.planners().find(planner => Number(planner.id) === Number(this.clientId)) || this.project?.client;
    return effectiveUtmParams(this.addProjectForm?.get('google_utm_params')?.value, client);
  }

  private getProjectWidgetSettings(): any {
    if (!this.project?.widget_settings) {
      return {};
    }
    return typeof this.project.widget_settings === 'string'
      ? JSON.parse(this.project.widget_settings)
      : {...this.project.widget_settings};
  }

  private patchProject() {
    if (!this.addProjectForm || !this.project) {
      return;
    }
    const project = this.project;
    const widgetSettings = this.getProjectWidgetSettings();

    let statsExclude: any = project.stats_exclude;
    while (typeof statsExclude === 'string') {
      statsExclude = JSON.parse(statsExclude);
    }
    const joinList = (list: unknown): string => Array.isArray(list)
      ? list.map(item => String(item ?? '').trim()).filter(Boolean).join(',')
      : '';

    const dates = (project.dates || []).map(date => ({
      important_text: date.important_text || '',
      important_date: toDay(date.important_date)
    }));
    const socials = (project.socials || []).map(social => ({
      social_label: social.label || '',
      social_value: social.value || ''
    }));

    this.addProjectForm.patchValue({
      ...project,
      industry: project.industry ? String(project.industry) : '',
      currency: project.currency || 'USD',
      timezone: project.timezone || null,
      start_date: toDay(project.start_date),
      start_time: toTime(project.start_time),
      end_date: toDay(project.end_date),
      end_time: toTime(project.end_time),
      activation_starts_at: toDay(project.activation_starts_at),
      activation_ends_at: toDay(project.activation_ends_at),
      register_average_price: project.register_average_price ?? '',
      report_analysis: project.report_analysis || '',
      dates: dates.length ? dates : [{...this.importantDateEmptyItem}],
      socials: socials.length ? socials : [{...this.socialEmptyItem}],
      exclude_emails: joinList(statsExclude?.emails),
      exclude_ips: joinList(statsExclude?.ips),
      widget_cdn_enabled: toInheritedOption(this.normalizeInheritedBoolean(widgetSettings.widget_cdn_enabled)),
      widget_version: toInheritedOption(this.normalizeInheritedWidgetVersion(widgetSettings.widget_version)),
      rendering_system: toInheritedOption(project.rendering_system)
    });

    this.step.params = {
      projectTitle: project.title,
      projectId: project.id,
    };
  }

  generateSlug(): void {
    this.slugdata = this.convertToSlug(this.addProjectForm.get('title').value.trim());
    this.addProjectForm.get('slug').setValue(this.slugdata);
    this.addProjectForm.get('hashtag').setValue(this.slugdata);
  }

  onClientChange(clientId: unknown): void {
    this.addProjectForm.controls['google_utm_params'].setValue(null);
    if (clientId) {
      this.validateSlug();
    } else {
      this.loadAllPlanners();
    }
  }

  convertToSlug(Text) {
    return Text.toLowerCase().replace(/[^\w ]+/g, '').replace(/ +/g, '-');
  }

  public validateSlug() {
    if (!this.addProjectForm.get('slug').value) {
      return;
    }
    this.addProjectForm.get('hashtag').setValue(this.addProjectForm.get('slug').value);
    const projectId = this.projectId();
    this.projectStore.dispatch(ProjectValidateSlug({
      params: {
        slug: this.addProjectForm.get('slug').value,
        client_id: this.addProjectForm.get('client_id').value,
        ...(projectId ? {project_id: projectId} : {})
      }
    }));
  }

  onCancel() {
    if (this.isModal()) {
      this.modalDone.emit('cancelled');
      return;
    }
    if (this.returnUrl && this.returnUrl !== this.router.url) {
      this.router.navigateByUrl(this.returnUrl);
    } else {
      this.router.navigate(['projects']);
    }
  }

  addProject() {
    this.submitted = true;
    if (this.addProjectForm.invalid) {
      setTimeout(() => {
        this.scrollToField();
      }, 500);
      return;
    }

    this.addProjectData();
  }

  addProjectData() {
    const value: any = this.addProjectForm.value;
    const splitList = (list: string): string[] => String(list || '')
      .split(',').map(item => item.trim()).filter(Boolean);
    const params: any = {
      ...value,
      stats_exclude: {
        emails: splitList(value.exclude_emails),
        ips: splitList(value.exclude_ips),
      },
      // Only auto-set client_id if user has the permission AND their client_id exists AND no client was selected
      ...(!this.projectId()
        && this.currentUser()?.can('auto-set-client', 'project')
        && this.currentUser()?.client_id
        && !value.client_id
        ? {client_id: this.currentUser().client_id} : {}),
    };
    if (!params.client_id) {
      delete params.client_id;
    }

    // Dates are the calendar days the form shows, as YYYY-MM-DD; times are the project's wall clock.
    params.timezone = value.timezone || null;
    params.start_time = params.start_date ? toTime(value.start_time) : '';
    params.end_time = params.end_date ? toTime(value.end_time) : '';
    params.register_average_price = value.register_average_price === '' || value.register_average_price === null
      ? null : Number(value.register_average_price);
    params.report_analysis = value.report_analysis || null;
    delete params.exclude_emails;
    delete params.exclude_ips;

    params.widget_settings = {
      ...(this.projectId() ? this.getProjectWidgetSettings() : {}),
      widget_version: this.normalizeInheritedWidgetVersion(value.widget_version),
      widget_cdn_enabled: this.normalizeInheritedBoolean(value.widget_cdn_enabled)
    };
    delete params.widget_cdn_enabled;
    delete params.widget_version;

    // Only super-admin can set rendering_system
    if (this.isSuperAdmin) {
      params.rendering_system = value.rendering_system === INHERIT ? null : value.rendering_system;
    } else {
      delete params.rendering_system;
    }

    const projectId = this.projectId();
    if (projectId) {
      this.projectStore.dispatch(ProjectUpdate({project: params, projectId: projectId}));
    } else {
      this.projectStore.dispatch(ProjectCreate({project: params}));
    }
  }

  // START: Function used to fetch industries list.
  public getIndustries() {
    this.projectStore.dispatch(ProjectIndustries());
  }

  private loadCurrencies() {
    this.projectService.getCurrencies()
      .pipe(takeUntil(this.unsubscriber))
      .subscribe({
        next: (resp: any) => {
          const list: CurrencyOption[] = resp?.data?.result || resp?.data || [];
          this.currencyList.set(list.map(c => ({code: c.code, name: `${c.code} — ${c.name}`})));
        },
        error: () => this.currencyList.set([])
      });
  }

  loadAllPlanners(search = '', page = 0, perPage = 10, orderBy= {column: 'name', dir: 'asc'}, fields = ['id', 'name']) {
    this.clientStore.dispatch(ClientsListAll({
      params: {
        options: JSON.stringify({includePackage: true, includePagination: false, orderBy, fields}),
        search
      }
    }));
  }

  scrollToField() {
    const invalid = this.elRef.nativeElement.querySelector('.invalid-text');
    if (invalid) {
      this.toastr.warning('Some fields have error, please review', 'warning');
      window.scrollBy({
        top: invalid.getBoundingClientRect().top - 150,
        left: 0,
        behavior: 'smooth'
      });
    }
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }
}
